import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { Button } from '../../../../shared/components/index.js';
import {
  DEFAULT_PAINTER_PRIORITY,
  DEFAULT_PAINTER_REASSIGN_REASON,
  M08_PAINTER_PRIORITIES,
  M08_PAINTER_REASSIGN_REASONS,
  type M08PainterPriority,
  type M08PainterReassignReason,
} from '../../../../shared/contracts/backend-enums.js';
import { painterStatusLabel } from '../../../../shared/contracts/painter-status.js';
import { JobAssignmentSummary, type AssignmentSummary } from './JobAssignmentSummary.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/**
 * M08-006 / M08-007 §12–15 — Painter assignment + reassignment for a job.
 *
 * Routed by the vendor assignment id; resolves jobRequestId from the
 * assignment detail. Lists the vendor's whole field force who can take the
 * job, best suited first, by name and code
 * (GET /v1/painter-assignments/suggest), the current painter assignments
 * (GET /v1/painter-assignments), assigns a painter (POST /v1/painter-assignments)
 * and reassigns one the server marks canReassign — handed back, or left
 * unanswered past the painter's acceptance time
 * (POST /v1/painter-assignments/:id/reassign).
 */
interface Suggestion {
  readonly painterUserId: string;
  readonly displayName: string;
  readonly painterCode: string;
  readonly score: number;
  readonly breakdown: { readonly performance: number; readonly load: number; readonly territory: number; readonly workType: number };
}

interface PainterAssignment {
  readonly painterAssignmentId: string;
  readonly painterUserId: string;
  readonly painterName: string | null;
  readonly painterCode: string | null;
  readonly expectedStartDate: string;
  readonly expectedEndDate: string;
  readonly priority: string;
  readonly status: string;
  /** The server allows handing this job to another painter now (RA-1). */
  readonly canReassign: boolean;
  /** Holds the job — in progress, or finished and awaiting submission. */
  readonly holdsJob: boolean;
}

/** Job context from GET /v1/vendor-assignments/:id — see AssignmentJobContext. */
export function VendorPainterPage(): ReactNode {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { dayRange } = useBusinessDay();

  const [jobRequestId, setJobRequestId] = useState<string | null>(null);
  const [header, setHeader] = useState<AssignmentSummary | null>(null);
  const [suggestions, setSuggestions] = useState<readonly Suggestion[]>([]);
  const [assignments, setAssignments] = useState<readonly PainterAssignment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Assign form.
  const [painterUserId, setPainterUserId] = useState('');
  const [expectedStartDate, setExpectedStartDate] = useState('');
  const [expectedEndDate, setExpectedEndDate] = useState('');
  const [priority, setPriority] = useState<M08PainterPriority>(DEFAULT_PAINTER_PRIORITY);
  const [instructions, setInstructions] = useState('');
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignOk, setAssignOk] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reassign form.
  const [reassignFor, setReassignFor] = useState<string | null>(null);
  const [reNewPainter, setReNewPainter] = useState('');
  const [reReason, setReReason] = useState<M08PainterReassignReason>(DEFAULT_PAINTER_REASSIGN_REASON);
  const [reRemarks, setReRemarks] = useState('');
  const [reError, setReError] = useState<string | null>(null);

  const loadLists = useCallback(async (job: string) => {
    const [sug, list] = await Promise.all([
      apiRequest<{ topN: number | null; results: Suggestion[] }>(`/v1/painter-assignments/suggest?jobRequestId=${encodeURIComponent(job)}`, { token }),
      apiRequest<{ items: PainterAssignment[] }>(`/v1/painter-assignments?jobRequestId=${encodeURIComponent(job)}`, { token }),
    ]);
    setSuggestions(sug.results);
    setAssignments(list.items);
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const asg = await apiRequest<AssignmentSummary>(`/v1/vendor-assignments/${assignmentId}`, { token });
        if (!alive) return;
        setHeader(asg);
        setJobRequestId(asg.jobRequestId);
        await loadLists(asg.jobRequestId);
        if (alive) setLoaded(true);
      } catch (e) {
        if (alive) { setError((e as Error).message); setLoaded(true); }
      }
    })();
    return () => { alive = false; };
  }, [assignmentId, token, loadLists]);

  async function assign() {
    setAssignError(null);
    if (!painterUserId) { setAssignError(t('vops.painter.painterRequired')); return; }
    if (!expectedStartDate || !expectedEndDate) { setAssignError(t('vops.painter.datesRequired')); return; }
    setBusy(true);
    try {
      await apiRequest('/v1/painter-assignments', {
        method: 'POST',
        token,
        body: {
          jobRequestId,
          vendorAssignmentId: assignmentId,
          painterUserId,
          expectedStartDate,
          expectedEndDate,
          priority,
          instructions: instructions.trim() || null,
        },
      });
      setAssignOk(true);
      setPainterUserId('');
      setInstructions('');
      if (jobRequestId) await loadLists(jobRequestId);
    } catch (e) {
      setAssignError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function reassign(currentId: string) {
    setReError(null);
    if (!reNewPainter) { setReError(t('vops.painter.newPainterRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/painter-assignments/${currentId}/reassign`, {
        method: 'POST',
        token,
        body: { painterUserId: reNewPainter, reasonCode: reReason, remarks: reRemarks.trim() || null },
      });
      setReassignFor(null);
      setReNewPainter('');
      setReRemarks('');
      if (jobRequestId) await loadLists(jobRequestId);
    } catch (e) {
      setReError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <main aria-label="vendor-painter"><p>{t('common.loading')}</p></main>;

  // A job carries one painter until the vendor submits it (EX-M08-12) — his
  // finished work awaiting submission included. Offering the assign form while
  // one holds it invited the vendor to fill it in and give the same job twice.
  const activeAssignment = assignments.find((p) => p.holdsJob) ?? null;
  // The list now names its own painter; suggestions are only a fallback for a
  // master row that has since gone.
  const painterLabel = (p: PainterAssignment): string =>
    p.painterName ??
    suggestions.find((s) => s.painterUserId === p.painterUserId)?.displayName ??
    p.painterUserId;

  return (
    <main aria-label="vendor-painter">
      <h1>{t('vops.painter.title')}</h1>
      <nav aria-label="painter-nav" className="tl-toolbar">
        <Link className="tl-btn tl-btn--secondary" to={`/vendor-ops/jobs/${assignmentId}`} aria-label="back-to-job">{t('vops.common.backToJob')}</Link>
      </nav>

      {error ? <p role="alert">{error}</p> : null}

      <JobAssignmentSummary assignmentId={assignmentId} header={header} />

      <section aria-label="painter-current">
        <h2>{t('vops.painter.current')}</h2>
        {assignments.length === 0 ? (
          <p aria-label="painter-current-empty">{t('vops.painter.currentEmpty')}</p>
        ) : (
          <ul aria-label="painter-list">
            {assignments.map((p) => (
              <li key={p.painterAssignmentId} aria-label={`painter-${p.painterAssignmentId}`}>
                <span aria-label={`painter-user-${p.painterAssignmentId}`}>{painterLabel(p)}{p.painterCode ? ` · ${p.painterCode}` : ''}</span>{' · '}
                <span aria-label={`painter-status-${p.painterAssignmentId}`}>{painterStatusLabel(t, p.status)}</span>{' · '}
                {dayRange(p.expectedStartDate, p.expectedEndDate)}
                {p.canReassign ? (
                  reassignFor === p.painterAssignmentId ? (
                    <div>
                      <label htmlFor={`re-painter-${p.painterAssignmentId}`}>{t('vops.painter.newPainter')}</label>
                      <select
                        id={`re-painter-${p.painterAssignmentId}`}
                        aria-label={`reassign-painter-${p.painterAssignmentId}`}
                        value={reNewPainter}
                        onChange={(e) => setReNewPainter(e.target.value)}
                        disabled={busy}
                      >
                        <option value="">{t('vops.painter.selectPainter')}</option>
                        {suggestions.map((s) => (
                          <option key={s.painterUserId} value={s.painterUserId}>{personLabel(s.displayName, s.painterCode)}</option>
                        ))}
                      </select>
                      <label htmlFor={`re-reason-${p.painterAssignmentId}`}>{t('vops.common.reason')}</label>
                      <select
                        id={`re-reason-${p.painterAssignmentId}`}
                        aria-label={`reassign-reason-${p.painterAssignmentId}`}
                        value={reReason}
                        onChange={(e) => setReReason(e.target.value as M08PainterReassignReason)}
                        disabled={busy}
                      >
                        {M08_PAINTER_REASSIGN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <label htmlFor={`re-remarks-${p.painterAssignmentId}`}>{t('vops.common.remarksOptional')}</label>
                      <textarea
                        id={`re-remarks-${p.painterAssignmentId}`}
                        aria-label={`reassign-remarks-${p.painterAssignmentId}`}
                        value={reRemarks}
                        onChange={(e) => setReRemarks(e.target.value)}
                        disabled={busy}
                      />
                      {reError ? <p role="alert">{reError}</p> : null}
                      <Button
                        type="button"
                        aria-label={`reassign-submit-${p.painterAssignmentId}`}
                        onClick={() => reassign(p.painterAssignmentId)}
                        disabled={busy}
                        variant="primary"
                      >
                        {t('vops.painter.confirmReassign')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      aria-label={`reassign-open-${p.painterAssignmentId}`}
                      onClick={() => { setReassignFor(p.painterAssignmentId); setReNewPainter(''); setReRemarks(''); setReError(null); }}
                      variant="secondary"
                    >
                      {t('vops.painter.reassign')}
                    </Button>
                  )
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="painter-assign">
        <h2>{t('vops.painter.assign')}</h2>
        {assignOk ? <p role="status" aria-label="assign-result">{t('vops.painter.assigned')}</p> : null}
        {assignError ? <p role="alert">{assignError}</p> : null}

        {activeAssignment ? (
          <p aria-label="assign-blocked">
            {t('vops.painter.alreadyAssigned', {
              name: painterLabel(activeAssignment),
              status: activeAssignment.status,
            })}
          </p>
        ) : (
        <>
        <label htmlFor="assign-painter">{t('vops.painter.suggested')}</label>
        <select
          id="assign-painter"
          aria-label="assign-painter"
          value={painterUserId}
          onChange={(e) => setPainterUserId(e.target.value)}
          disabled={busy}
        >
          <option value="">{t('vops.painter.selectPainter')}</option>
          {suggestions.map((s) => (
            <option key={s.painterUserId} value={s.painterUserId}>
              {personLabel(s.displayName, s.painterCode)}
            </option>
          ))}
        </select>

        <label htmlFor="assign-start">{t('vops.painter.expectedStart')}</label>
        <DateInput id="assign-start" type="date" aria-label="assign-start" value={expectedStartDate}
               onValueChange={(nextValue) => setExpectedStartDate(nextValue)} disabled={busy} />

        <label htmlFor="assign-end">{t('vops.painter.expectedEnd')}</label>
        <DateInput id="assign-end" type="date" aria-label="assign-end" value={expectedEndDate}
               onValueChange={(nextValue) => setExpectedEndDate(nextValue)} disabled={busy} />

        <label htmlFor="assign-priority">{t('vops.common.priority')}</label>
        <select id="assign-priority" aria-label="assign-priority" value={priority}
                onChange={(e) => setPriority(e.target.value as M08PainterPriority)} disabled={busy}>
          {M08_PAINTER_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>

        <label htmlFor="assign-instructions">{t('vops.painter.instructions')}</label>
        <textarea id="assign-instructions" aria-label="assign-instructions" value={instructions}
                  onChange={(e) => setInstructions(e.target.value)} disabled={busy} />

        <Button type="button" aria-label="assign-btn" onClick={assign} disabled={busy} variant="primary">{t('vops.painter.assignBtn')}</Button>
        </>
        )}
      </section>
    </main>
  );
}
